import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgIf } from '@angular/common';
import { FoodTone } from '../../core/models/menu.model';
import { StorefrontService } from '../../core/services/storefront.service';
import { IconComponent } from './icon.component';
import { toneDef } from './food-tones';

/**
 * Real photo (optionally with a looping video over it — the photo is dropped once the video plays) and a fallback painted behind it. When there's no API image, the
 * brand (Sonic) logo is shown; only if the brand has no logo do we fall back to
 * the appetizing tone gradient. Size is controlled by the host element.
 */
@Component({
  selector: 'app-img-slot',
  standalone: true,
  imports: [NgIf, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [':host{display:block;position:relative}'],
  template: `<!-- brand-logo fallback (no API photo) -->
    <div *ngIf="(!src || failed) && logo && !videoOn" [style.border-radius.px]="radius"
      style="position:absolute;inset:0;overflow:hidden;background:var(--fr-surface-3);display:flex;align-items:center;justify-content:center">
      <img [src]="logo" alt="" [style.object-fit]="fallbackIsPhoto ? 'cover' : 'fill'" style="width:100%;height:100%;display:block" onerror="this.style.display='none'">
    </div>

    <!-- tone gradient (only when there's no photo AND no brand logo) -->
    <div *ngIf="(!src || failed) && !logo && !videoOn" [style.border-radius.px]="radius" [style.background]="def.g"
      style="position:absolute;inset:0;overflow:hidden">
      <div style="position:absolute;inset:0;background:radial-gradient(80% 60% at 50% 120%, rgba(0,0,0,0.34), transparent 70%)"></div>
      <div style="position:absolute;inset:0;opacity:0.16;display:flex;align-items:center;justify-content:center">
        <app-icon [name]="def.icon" [size]="iconSize" color="#fff"></app-icon>
      </div>
      <div style="position:absolute;top:0;left:0;right:0;height:45%;background:linear-gradient(180deg, rgba(255,255,255,0.2), transparent)"></div>
    </div>

    <!-- API photo (removed once a video is playing over it) -->
    <img *ngIf="src && !videoOn" [src]="src" alt="" (error)="failed = true" [hidden]="failed"
      [style.border-radius.px]="radius"
      [style.object-fit]="fit" style="position:absolute;inset:0;width:100%;height:100%;display:block">

    <!-- optional video over the photo (hero): muted + inline so it can autoplay; the photo is its poster.
         Once it plays, the photo/fallback underneath is dropped; if it fails, they come back. -->
    <video *ngIf="video && !videoFailed" [src]="video" [attr.poster]="src || null" autoplay muted loop playsinline preload="metadata"
      (playing)="videoPlaying = true" (error)="videoFailed = true"
      [muted]="true" [style.border-radius.px]="radius"
      style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center;display:block"></video>

    <!-- scrim only over a real photo -->
    <div *ngIf="scrim && ((src && !failed) || video)" [style.border-radius.px]="radius"
      style="position:absolute;inset:0;pointer-events:none;background:radial-gradient(85% 65% at 50% 118%, rgba(0,0,0,0.30), transparent 70%)"></div>
    <ng-content></ng-content>`,
})
export class ImgSlotComponent {
  @Input() src?: string | null;
  /** Optional looping video shown over the photo (the photo stays as poster and fallback). */
  @Input() set video(v: string | null | undefined) {
    if (v !== this._video) { this.videoPlaying = false; this.videoFailed = false; } // a new video starts over
    this._video = v;
  }
  get video(): string | null | undefined { return this._video; }
  private _video?: string | null;
  @Input() tone: FoodTone = 'beef';
  @Input() radius = 14;
  @Input() iconSize = 60;
  @Input() scrim = true;
  /** How the API photo fills the box: 'fill' (default, stretches), 'cover' (crops), 'contain'. */
  @Input() fit: 'fill' | 'cover' | 'contain' = 'fill';
  failed = false;
  /** The video has started playing (so nothing needs to show underneath it). */
  videoPlaying = false;
  videoFailed = false;
  get videoOn(): boolean { return !!this.video && this.videoPlaying && !this.videoFailed; }

  constructor(private sf: StorefrontService) {}

  /** Shown when the item has no photo: the default item image, else the brand logo. */
  get logo(): string { return this.sf.brand?.itemImage || this.sf.brand?.logo || ''; }
  get fallbackIsPhoto(): boolean { return !!this.sf.brand?.itemImageIsPhoto; }
  get def() { return toneDef(this.tone); }
}
